import { Project, AppUser, BookingRecord, Testimonial, AppSettings, Complaint, SimpleBlogPost, NewsletterSubscriber, BlogComment } from '../types';

// --- INITIAL MOCK DATA ---

// Static blog posts - CLEARED (Now using Firebase)
const INITIAL_POSTS: SimpleBlogPost[] = [];


const INITIAL_PROJECTS: Project[] = [
  // ==================== WEB DEVELOPMENT ====================
  {
    id: '1',
    title: "Nexus Finance Dashboard",
    category: "Web",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800",
    client: "Nexus Capital",
    year: "2024",
    description: "A high-performance decentralized trading platform built for institutional investors with real-time data streaming.",
    techStack: ["React", "TypeScript", "Node.js", "Redis", "WebSocket"],
    size: 'large',
    challenge: "Handling high-frequency trading data with sub-millisecond latency while maintaining a premium user experience for professional traders.",
    solution: "Implemented a WebSocket-based architecture with WebGL-powered charts and Redis caching for instant data delivery.",
    results: ["85% Faster Load", "$2B Trading Volume", "99.99% Uptime"],
    secondaryImages: ["https://images.unsplash.com/photo-1642790106117-e829e14a795f?auto=format&fit=crop&q=80&w=600"]
  },
  {
    id: '2',
    title: "Quantum E-Learning Platform",
    category: "Web",
    image: "https://images.unsplash.com/photo-1501504905252-473c47e087f8?auto=format&fit=crop&q=80&w=800",
    client: "EduTech Global",
    year: "2024",
    description: "Next-generation learning management system with AI-powered personalized curriculum and gamified progress tracking.",
    techStack: ["Next.js", "GraphQL", "PostgreSQL", "AWS", "OpenAI"],
    size: 'normal',
    challenge: "Creating an engaging learning experience that adapts to each student's pace and learning style.",
    solution: "Developed an AI recommendation engine that analyzes learning patterns and suggests optimized study paths.",
    results: ["150K Active Users", "40% Better Retention", "4.9★ Rating"],
    secondaryImages: ["https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&q=80&w=600"]
  },

  // ==================== MOBILE APPS ====================
  {
    id: '3',
    title: "HealthAI Diagnostic App",
    category: "Mobile",
    image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&q=80&w=800",
    client: "MediCare Plus",
    year: "2023",
    description: "AI-powered skin condition diagnosis with offline capability for remote healthcare workers.",
    techStack: ["Flutter", "TensorFlow Lite", "Firebase", "Python"],
    size: 'tall',
    challenge: "Providing accurate medical diagnostics in areas with limited internet connectivity.",
    solution: "Deployed edge computing ML models that run entirely on-device with 99% accuracy.",
    results: ["99% Accuracy", "5K+ Clinicians", "500K Diagnoses"],
    secondaryImages: ["https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&q=80&w=600"]
  },
  {
    id: '4',
    title: "SwiftRide Transportation",
    category: "Mobile",
    image: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&q=80&w=800",
    client: "Metro Transit Authority",
    year: "2024",
    description: "Real-time public transit app with live tracking, fare payments, and accessibility features.",
    techStack: ["React Native", "Node.js", "MongoDB", "Google Maps API"],
    size: 'normal',
    challenge: "Integrating multiple transit systems into a unified, easy-to-use mobile experience.",
    solution: "Built a unified API layer that aggregates data from 12 different transit providers in real-time.",
    results: ["2M+ Downloads", "35% Ridership Increase", "Accessibility Award"],
    secondaryImages: ["https://images.unsplash.com/photo-1556157382-97eda2d62296?auto=format&fit=crop&q=80&w=600"]
  },

  // ==================== AI & DATA ====================
  {
    id: '5',
    title: "Predictive Analytics Engine",
    category: "AI & Data",
    image: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&q=80&w=800",
    client: "Fortune 500 Retailer",
    year: "2024",
    description: "Machine learning pipeline for demand forecasting and inventory optimization across 5,000+ locations.",
    techStack: ["Python", "TensorFlow", "Spark", "AWS SageMaker", "Tableau"],
    size: 'large',
    challenge: "Reducing overstock and stockouts across a massive global supply chain.",
    solution: "Developed ensemble ML models that analyze 50+ variables for accurate demand prediction.",
    results: ["$50M Savings", "30% Less Waste", "95% Forecast Accuracy"],
    secondaryImages: ["https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=600"]
  },
  {
    id: '6',
    title: "AI Chatbot for Banking",
    category: "AI & Data",
    image: "https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&q=80&w=800",
    client: "National Bank Corp",
    year: "2024",
    description: "Conversational AI assistant handling 80% of customer queries with human-like understanding.",
    techStack: ["GPT-4", "Python", "FastAPI", "Redis", "Azure"],
    size: 'normal',
    challenge: "Reducing customer service costs while improving response quality and speed.",
    solution: "Fine-tuned LLM on banking-specific data with secure PII handling and escalation protocols.",
    results: ["80% Query Resolution", "60% Cost Reduction", "4.8★ CSAT Score"],
    secondaryImages: ["https://images.unsplash.com/photo-1535378620166-273708d44e4c?auto=format&fit=crop&q=80&w=600"]
  },

  // ==================== BRANDING & DESIGN ====================
  {
    id: '7',
    title: "Luxe Fashion Rebrand",
    category: "Branding",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&q=80&w=800",
    client: "Maison Élite",
    year: "2024",
    description: "Complete brand identity overhaul for a luxury fashion house including logo, packaging, and digital presence.",
    techStack: ["Figma", "Illustrator", "After Effects", "3D Rendering"],
    size: 'tall',
    challenge: "Modernizing a heritage brand without losing its established prestige and recognition.",
    solution: "Created a refined visual language that honors tradition while embracing contemporary aesthetics.",
    results: ["35% Sales Increase", "Vogue Feature", "Brand Award Winner"],
    secondaryImages: ["https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80&w=600"]
  },
  {
    id: '8',
    title: "TechStart Brand System",
    category: "Branding",
    image: "https://images.unsplash.com/photo-1561070791-2526d30994b5?auto=format&fit=crop&q=80&w=800",
    client: "TechStart Ventures",
    year: "2024",
    description: "Comprehensive design system and brand guidelines for a Silicon Valley VC firm.",
    techStack: ["Figma", "Brand Guidelines", "Motion Design", "UI Kit"],
    size: 'normal',
    challenge: "Creating a distinctive identity that appeals to both founders and institutional investors.",
    solution: "Developed a modular design system with flexible components for various investor communications.",
    results: ["100% Brand Consistency", "50+ Portfolio Companies", "LinkedIn Viral Post"],
    secondaryImages: ["https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&q=80&w=600"]
  },

  // ==================== E-COMMERCE ====================
  {
    id: '9',
    title: "Global Marketplace Platform",
    category: "Web",
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&q=80&w=800",
    client: "ArtisanHub",
    year: "2024",
    description: "Multi-vendor e-commerce platform connecting 10,000+ artisans with global customers.",
    techStack: ["Shopify Plus", "React", "Stripe", "Algolia", "Node.js"],
    size: 'large',
    challenge: "Building a scalable platform that handles high traffic while supporting diverse payment methods worldwide.",
    solution: "Implemented headless commerce architecture with intelligent search and multi-currency support.",
    results: ["$25M GMV Year 1", "10K+ Sellers", "98% Customer Satisfaction"],
    secondaryImages: ["https://images.unsplash.com/photo-1472851294608-062f824d29cc?auto=format&fit=crop&q=80&w=600"]
  },
  {
    id: '10',
    title: "Luxury Watch E-Store",
    category: "Web",
    image: "https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&q=80&w=800",
    client: "Chronos Timepieces",
    year: "2024",
    description: "Premium e-commerce experience for high-end watches with 3D configurator and AR try-on.",
    techStack: ["WooCommerce", "Three.js", "WebAR", "PHP", "MySQL"],
    size: 'normal',
    challenge: "Replicating the in-store luxury experience in a digital environment.",
    solution: "Created immersive 3D product visualization with virtual try-on using augmented reality.",
    results: ["65% Conversion Lift", "$5M Online Sales", "Webby Award Nominee"],
    secondaryImages: ["https://images.unsplash.com/photo-1434056886845-dbd39c1cc727?auto=format&fit=crop&q=80&w=600"]
  },

  // ==================== SOFTWARE & SYSTEMS ====================
  {
    id: '11',
    title: "Enterprise ERP Solution",
    category: "Web",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800",
    client: "ManufactureCo",
    year: "2023",
    description: "Custom ERP system managing inventory, production, and accounting for 15 factories.",
    techStack: ["Java", "Spring Boot", "React", "PostgreSQL", "Docker"],
    size: 'normal',
    challenge: "Replacing legacy systems while maintaining 24/7 manufacturing operations.",
    solution: "Phased migration with real-time data synchronization ensuring zero production downtime.",
    results: ["40% Efficiency Gain", "Zero Downtime", "15 Sites Deployed"],
    secondaryImages: ["https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&q=80&w=600"]
  },
  {
    id: '12',
    title: "Healthcare CRM Platform",
    category: "Web",
    image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80&w=800",
    client: "HealthFirst Network",
    year: "2024",
    description: "HIPAA-compliant patient relationship management system for a network of 200+ clinics.",
    techStack: ["React", "Node.js", "MongoDB", "AWS", "HIPAA Compliance"],
    size: 'normal',
    challenge: "Managing sensitive patient data across multiple locations while ensuring regulatory compliance.",
    solution: "Built end-to-end encrypted platform with audit trails and automatic compliance reporting.",
    results: ["HIPAA Certified", "200+ Clinics", "50% Admin Time Saved"],
    secondaryImages: ["https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&q=80&w=600"]
  },

  // ==================== CYBERSECURITY ====================
  {
    id: '13',
    title: "Zero Trust Security Framework",
    category: "Web",
    image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=800",
    client: "FinSecure Bank",
    year: "2024",
    description: "Enterprise-wide cybersecurity overhaul implementing zero-trust architecture.",
    techStack: ["Azure AD", "Okta", "CrowdStrike", "Splunk", "Terraform"],
    size: 'normal',
    challenge: "Securing 50,000+ endpoints across a global financial institution.",
    solution: "Deployed comprehensive zero-trust model with continuous verification and micro-segmentation.",
    results: ["Zero Breaches", "SOC 2 Certified", "95% Threat Detection"],
    secondaryImages: ["https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&q=80&w=600"]
  },
  {
    id: '14',
    title: "Penetration Testing Suite",
    category: "AI & Data",
    image: "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&q=80&w=800",
    client: "CyberShield Inc",
    year: "2024",
    description: "Automated penetration testing platform using AI to identify vulnerabilities.",
    techStack: ["Python", "Kali Linux", "Machine Learning", "REST APIs"],
    size: 'normal',
    challenge: "Scaling security assessments for hundreds of client applications efficiently.",
    solution: "AI-powered vulnerability scanner that learns from previous assessments to improve accuracy.",
    results: ["10x Faster Audits", "500+ Clients", "99.5% Detection Rate"],
    secondaryImages: ["https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?auto=format&fit=crop&q=80&w=600"]
  },

  // ==================== VIDEO & ANIMATION ====================
  {
    id: '15',
    title: "Product Launch Campaign",
    category: "Branding",
    image: "https://images.unsplash.com/photo-1536240478700-b869070f9279?auto=format&fit=crop&q=80&w=800",
    client: "NovaTech Devices",
    year: "2024",
    description: "Cinematic product reveal video and 3D animations for flagship smartphone launch.",
    techStack: ["After Effects", "Cinema 4D", "Premiere Pro", "DaVinci Resolve"],
    size: 'large',
    challenge: "Creating viral-worthy content that showcases product innovation in 60 seconds.",
    solution: "Produced high-end CGI animations with dramatic lighting and seamless product transitions.",
    results: ["50M Views", "Viral on Twitter", "Best Tech Ad Award"],
    secondaryImages: ["https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?auto=format&fit=crop&q=80&w=600"]
  },
  {
    id: '16',
    title: "Corporate Explainer Series",
    category: "Branding",
    image: "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?auto=format&fit=crop&q=80&w=800",
    client: "CloudSync Solutions",
    year: "2024",
    description: "Series of animated explainer videos simplifying complex B2B software products.",
    techStack: ["Motion Graphics", "Lottie", "Illustrator", "After Effects"],
    size: 'normal',
    challenge: "Making technical cloud infrastructure concepts accessible to non-technical buyers.",
    solution: "Created engaging character-driven animations with clear visual metaphors.",
    results: ["300% Demo Requests", "5 Video Series", "LinkedIn Best Content"],
    secondaryImages: ["https://images.unsplash.com/photo-1611532736597-de2d4265fba3?auto=format&fit=crop&q=80&w=600"]
  },

  // ==================== CLOUD & DEVOPS ====================
  {
    id: '17',
    title: "Multi-Cloud Infrastructure",
    category: "Web",
    image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=800",
    client: "GlobalStream Media",
    year: "2024",
    description: "Hybrid cloud architecture supporting 100M+ monthly active users with 99.999% uptime.",
    techStack: ["AWS", "GCP", "Kubernetes", "Terraform", "Prometheus"],
    size: 'normal',
    challenge: "Ensuring ultra-high availability for live streaming events with millions of concurrent viewers.",
    solution: "Implemented multi-region failover with auto-scaling and intelligent load balancing.",
    results: ["99.999% Uptime", "100M MAU", "60% Cost Reduction"],
    secondaryImages: ["https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&q=80&w=600"]
  },
  {
    id: '18',
    title: "CI/CD Pipeline Modernization",
    category: "Web",
    image: "https://images.unsplash.com/photo-1618401471353-b98afee0b2eb?auto=format&fit=crop&q=80&w=800",
    client: "DevOps Dynamics",
    year: "2024",
    description: "End-to-end DevOps transformation enabling 100+ daily deployments with zero downtime.",
    techStack: ["GitHub Actions", "ArgoCD", "Docker", "Helm", "DataDog"],
    size: 'normal',
    challenge: "Accelerating release cycles while maintaining enterprise-grade reliability.",
    solution: "Built automated pipelines with comprehensive testing, security scanning, and rollback capabilities.",
    results: ["100+ Daily Deploys", "90% Faster Releases", "Zero Failed Deployments"],
    secondaryImages: ["https://images.unsplash.com/photo-1607799279861-4dd421887fb3?auto=format&fit=crop&q=80&w=600"]
  },

  // ==================== MARKETING & GROWTH ====================
  {
    id: '19',
    title: "Growth Marketing Campaign",
    category: "Branding",
    image: "https://images.unsplash.com/photo-1533750516457-a7f992034fec?auto=format&fit=crop&q=80&w=800",
    client: "FitLife App",
    year: "2024",
    description: "Full-funnel digital marketing campaign driving 500K app downloads in 3 months.",
    techStack: ["Google Ads", "Meta Ads", "TikTok", "Analytics", "Hotjar"],
    size: 'normal',
    challenge: "Acquiring users cost-effectively in a saturated fitness app market.",
    solution: "Data-driven creative testing with hyper-targeted audience segmentation.",
    results: ["500K Downloads", "$2.50 CPA", "300% ROAS"],
    secondaryImages: ["https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=600"]
  },
  {
    id: '20',
    title: "SEO Domination Strategy",
    category: "Web",
    image: "https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?auto=format&fit=crop&q=80&w=800",
    client: "LegalEase Firm",
    year: "2024",
    description: "Comprehensive SEO overhaul achieving #1 rankings for 200+ high-intent keywords.",
    techStack: ["Ahrefs", "SEMrush", "Technical SEO", "Content Strategy"],
    size: 'normal',
    challenge: "Outranking established competitors in the competitive legal services market.",
    solution: "Strategic content clusters, technical optimizations, and authoritative backlink acquisition.",
    results: ["#1 for 200+ Keywords", "400% Organic Traffic", "$2M+ Lead Value"],
    secondaryImages: ["https://images.unsplash.com/photo-1553484771-371a605b060b?auto=format&fit=crop&q=80&w=600"]
  },

  // ==================== NEXT-GEN / WEB3 ====================
  {
    id: '21',
    title: "NFT Marketplace Platform",
    category: "Web",
    image: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?auto=format&fit=crop&q=80&w=800",
    client: "ArtChain Collective",
    year: "2024",
    description: "Premium NFT marketplace with lazy minting, auctions, and multi-chain support.",
    techStack: ["Solidity", "React", "ethers.js", "IPFS", "The Graph"],
    size: 'tall',
    challenge: "Creating a user-friendly Web3 experience for mainstream art collectors.",
    solution: "Abstracted blockchain complexity with familiar e-commerce UX patterns.",
    results: ["$10M Trading Volume", "50K Users", "Featured in Forbes"],
    secondaryImages: ["https://images.unsplash.com/photo-1620321023374-d1a68fbc720d?auto=format&fit=crop&q=80&w=600"]
  },
  {
    id: '22',
    title: "DeFi Yield Dashboard",
    category: "AI & Data",
    image: "https://images.unsplash.com/photo-1642790551116-18e150f248e5?auto=format&fit=crop&q=80&w=800",
    client: "YieldMax Protocol",
    year: "2024",
    description: "Real-time DeFi analytics dashboard tracking yields across 50+ protocols.",
    techStack: ["React", "Web3.js", "Python", "Redis", "TheGraph"],
    size: 'normal',
    challenge: "Aggregating data from disparate blockchain protocols with varying APIs.",
    solution: "Built unified data layer with real-time websocket updates and historical analytics.",
    results: ["$500M TVL Tracked", "15K Active Users", "DeFi Pulse Featured"],
    secondaryImages: ["https://images.unsplash.com/photo-1621761191319-c6fb62004040?auto=format&fit=crop&q=80&w=600"]
  },

  // ==================== SOCIAL & INFLUENCER ====================
  {
    id: '23',
    title: "Influencer Campaign Platform",
    category: "Web",
    image: "https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?auto=format&fit=crop&q=80&w=800",
    client: "InfluenceHub",
    year: "2024",
    description: "End-to-end influencer marketing platform connecting brands with 100K+ creators.",
    techStack: ["Next.js", "Python", "AI Matching", "Stripe", "PostgreSQL"],
    size: 'normal',
    challenge: "Matching brands with the right influencers based on authentic engagement.",
    solution: "AI-powered matching algorithm analyzing content, audience, and performance metrics.",
    results: ["100K+ Creators", "$50M Campaigns", "98% Match Success"],
    secondaryImages: ["https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?auto=format&fit=crop&q=80&w=600"]
  },
  {
    id: '24',
    title: "Social Analytics Dashboard",
    category: "AI & Data",
    image: "https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?auto=format&fit=crop&q=80&w=800",
    client: "BrandWatch Agency",
    year: "2024",
    description: "AI-powered social listening and sentiment analysis across all major platforms.",
    techStack: ["Python", "NLP", "React", "BigQuery", "TensorFlow"],
    size: 'normal',
    challenge: "Processing millions of social mentions in real-time with accurate sentiment classification.",
    solution: "Fine-tuned NLP models with streaming data pipelines for instant insights.",
    results: ["10M+ Posts/Day", "95% Accuracy", "Enterprise Clients"],
    secondaryImages: ["https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=600"]
  },

  // ==================== UI/UX DESIGN ====================
  {
    id: '25',
    title: "Banking App Redesign",
    category: "Mobile",
    image: "https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?auto=format&fit=crop&q=80&w=800",
    client: "NeoBanking Inc",
    year: "2024",
    description: "Complete mobile banking experience redesign with focus on accessibility and speed.",
    techStack: ["Figma", "Protopie", "User Research", "Design System"],
    size: 'normal',
    challenge: "Simplifying complex financial products for a Gen-Z audience.",
    solution: "Minimalist interface with gesture-based navigation and smart shortcuts.",
    results: ["4.9★ App Store", "200% DAU Increase", "Apple Design Award"],
    secondaryImages: ["https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&q=80&w=600"]
  },
  {
    id: '26',
    title: "SaaS Design System",
    category: "Branding",
    image: "https://images.unsplash.com/photo-1561070791-2526d30994b5?auto=format&fit=crop&q=80&w=800",
    client: "DataPro Analytics",
    year: "2024",
    description: "Comprehensive component library and design tokens for enterprise analytics platform.",
    techStack: ["Figma", "Storybook", "React", "CSS Variables", "Documentation"],
    size: 'normal',
    challenge: "Ensuring design consistency across 200+ unique interface screens.",
    solution: "Created modular design system with intelligent theming and accessibility built-in.",
    results: ["50% Faster Dev", "100% WCAG Compliant", "Open Sourced"],
    secondaryImages: ["https://images.unsplash.com/photo-1558655146-9f40138edfeb?auto=format&fit=crop&q=80&w=600"]
  },

  // ==================== AR/VR SOLUTIONS ====================
  {
    id: '27',
    title: "Virtual Showroom Experience",
    category: "Web",
    image: "https://images.unsplash.com/photo-1617802690992-15d93263d3a9?auto=format&fit=crop&q=80&w=800",
    client: "AutoLux Motors",
    year: "2024",
    description: "Immersive VR car configurator allowing customers to customize and explore vehicles.",
    techStack: ["WebXR", "Three.js", "React", "Blender", "AWS"],
    size: 'normal',
    challenge: "Replicating the in-dealership experience for remote car buyers.",
    solution: "Photorealistic VR environment with real-time customization and 360° exploration.",
    results: ["40% More Leads", "8 Min Avg Session", "Featured in TechCrunch"],
    secondaryImages: ["https://images.unsplash.com/photo-1503736334956-4c8f8e92946d?auto=format&fit=crop&q=80&w=600"]
  },
  {
    id: '28',
    title: "AR Training Platform",
    category: "Mobile",
    image: "https://images.unsplash.com/photo-1633356122544-f134324a6cee?auto=format&fit=crop&q=80&w=800",
    client: "Industrial Solutions Corp",
    year: "2024",
    description: "Augmented reality training app for equipment maintenance and safety procedures.",
    techStack: ["ARKit", "ARCore", "Unity", "Swift", "Kotlin"],
    size: 'normal',
    challenge: "Training factory workers on complex machinery without production downtime.",
    solution: "AR overlays providing step-by-step guidance on actual equipment.",
    results: ["70% Training Time Cut", "Zero Safety Incidents", "50K Workers Trained"],
    secondaryImages: ["https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=600"]
  }
];


const INITIAL_TESTIMONIALS: Testimonial[] = [
  {
    id: '1',
    name: "Alex Rivera",
    role: "Founder & CEO",
    company: "FinTech Pro",
    content: "Nescotech didn't just build a website; they transformed our entire digital presence. Our conversion rate tripled in just 3 months. Their team understood our vision from day one.",
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=100",
    rating: 5,
    status: 'approved',
    featured: true,
    date: '2025-08-15'
  },
  {
    id: '2',
    name: "Sarah Jenkins",
    role: "CMO",
    company: "RetailGiant",
    content: "The level of creativity and technical expertise is unmatched. They delivered a complex e-commerce platform ahead of schedule with zero bugs at launch.",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=100",
    rating: 5,
    status: 'approved',
    featured: true,
    date: '2025-09-02'
  },
  {
    id: '3',
    name: "Marcus Chen",
    role: "CTO",
    company: "DataFlow Systems",
    content: "A true partner in innovation. Their AI integration helped us automate 40% of our customer support workflow. ROI was visible within the first quarter.",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=100",
    rating: 5,
    status: 'approved',
    featured: true,
    date: '2025-09-10'
  },
  {
    id: '4',
    name: "Emily Dao",
    role: "Creative Director",
    company: "ArtSpace Gallery",
    content: "They captured our brand's soul perfectly. The liquid glass aesthetic they designed has become a benchmark in our industry. Absolutely stunning work!",
    avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&q=80&w=100",
    rating: 5,
    status: 'approved',
    featured: true,
    date: '2025-09-20'
  },
  {
    id: '5',
    name: "Michael Torres",
    role: "VP of Engineering",
    company: "CloudNine Tech",
    content: "From concept to deployment, Nescotech exceeded every expectation. Their attention to performance optimization resulted in 60% faster load times.",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=100",
    rating: 5,
    status: 'approved',
    featured: false,
    date: '2025-09-25'
  }
];

const INITIAL_USERS: AppUser[] = [
  {
    id: '1',
    name: 'Admin User',
    email: 'admin@nescotech.digital',
    role: 'admin',
    avatar: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&q=80&w=150',
    joinedDate: '2023-01-01',
    lastLogin: 'Just Now',
    status: 'active'
  },
  {
    id: '2',
    name: 'John Doe',
    email: 'john@example.com',
    phone: '+1 555 019 2834',
    role: 'user',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=150',
    joinedDate: '2025-09-15',
    lastLogin: '2 days ago',
    status: 'active',
    notes: 'High value client. Interested in AI solutions.'
  },
  {
    id: '3',
    name: 'Jane Smith',
    email: 'jane@client.com',
    role: 'user',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=150',
    joinedDate: '2025-09-20',
    lastLogin: '1 week ago',
    status: 'inactive'
  },
  {
    id: '4',
    name: 'Michael Scott',
    email: 'mike@paper.co',
    role: 'user',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=150',
    joinedDate: '2025-10-01',
    lastLogin: '3 hours ago',
    status: 'banned',
    notes: 'Payment failed multiple times.'
  }
];

const INITIAL_BOOKINGS: BookingRecord[] = [];

const INITIAL_SETTINGS: AppSettings = {
  general: {
    siteName: "Nescotech",
    tagline: "Architecting Digital Ecosystems",
    logoUrl: "",
    contactEmail: "hello@nescotech.digital",
    contactPhone: "+92 312 1528938",
    address: "100 Tech Plaza, Suite 400, NY"
  },
  theme: {
    primaryColor: "#008aff",
    secondaryColor: "#003a6b"
  },
  socials: {
    instagram: "https://www.instagram.com/nescotechsolutions/",
    twitter: "https://x.com/nesco_tech",
    linkedin: "https://www.linkedin.com/company/nescotech-solution/",
    facebook: "https://www.facebook.com/profile.php?id=61584742175591"
  },
  modules: {
    blog: true,
    portfolio: true,
    testimonials: true,
    booking: true,
    liveChat: true
  },
  integrations: {
    stripeKey: "",
    googleAnalyticsId: "",
    openaiKey: ""
  },
  maintenanceMode: false
};

// --- STORAGE MANAGER ---

const STORAGE_VERSION = '4.1';
const VERSION_KEY = 'nescotech_storage_version';

const checkVersion = () => {
  const currentVersion = localStorage.getItem(VERSION_KEY);
  if (currentVersion !== STORAGE_VERSION) {
    localStorage.removeItem('nescotech_testimonials');
    localStorage.removeItem('nescotech_projects');
    localStorage.removeItem('nescotech_settings'); // Force reload settings with new social URLs
    localStorage.setItem(VERSION_KEY, STORAGE_VERSION);
    console.log('Storage version updated. Settings, testimonials and projects reset to new data.');
  }
};

checkVersion();

const load = <T>(key: string, initial: T): T => {
  const stored = localStorage.getItem(key);
  if (!stored) {
    localStorage.setItem(key, JSON.stringify(initial));
    return initial;
  }
  return JSON.parse(stored);
};

const save = (key: string, data: any) => {
  localStorage.setItem(key, JSON.stringify(data));
  window.dispatchEvent(new Event('data-change'));
};

export const storage = {
  // BLOG POSTS
  getPosts: () => load<SimpleBlogPost[]>('nescotech_blog_posts', INITIAL_POSTS),
  savePosts: (posts: SimpleBlogPost[]) => save('nescotech_blog_posts', posts),
  addPost: (post: SimpleBlogPost) => {
    const posts = load<SimpleBlogPost[]>('nescotech_blog_posts', INITIAL_POSTS);
    save('nescotech_blog_posts', [post, ...posts]);
  },
  updatePost: (id: string, updates: Partial<SimpleBlogPost>) => {
    const posts = load<SimpleBlogPost[]>('nescotech_blog_posts', INITIAL_POSTS);
    const updated = posts.map(p => p.id === id ? { ...p, ...updates } : p);
    save('nescotech_blog_posts', updated);
  },
  deletePost: (id: string) => {
    const posts = load<SimpleBlogPost[]>('nescotech_blog_posts', INITIAL_POSTS);
    save('nescotech_blog_posts', posts.filter(p => p.id !== id));
  },
  addComment: (postId: string, comment: BlogComment) => {
    const posts = load<SimpleBlogPost[]>('nescotech_blog_posts', INITIAL_POSTS);
    const updated = posts.map(p => {
      if (p.id === postId) {
        return { ...p, comments: [comment, ...(p.comments || [])] };
      }
      return p;
    });
    save('nescotech_blog_posts', updated);
  },
  updateCommentStatus: (postId: string, commentId: string, status: 'pending' | 'approved') => {
    const posts = load<SimpleBlogPost[]>('nescotech_blog_posts', INITIAL_POSTS);
    const updated = posts.map(p => {
      if (p.id === postId && p.comments) {
        return {
          ...p,
          comments: p.comments.map(c => c.id === commentId ? { ...c, status } : c)
        };
      }
      return p;
    });
    save('nescotech_blog_posts', updated);
  },
  deleteComment: (postId: string, commentId: string) => {
    const posts = load<SimpleBlogPost[]>('nescotech_blog_posts', INITIAL_POSTS);
    const updated = posts.map(p => {
      if (p.id === postId && p.comments) {
        return {
          ...p,
          comments: p.comments.filter(c => c.id !== commentId)
        };
      }
      return p;
    });
    save('nescotech_blog_posts', updated);
  },

  // PROJECTS
  getProjects: () => load<Project[]>('nescotech_projects', INITIAL_PROJECTS),
  saveProjects: (projects: Project[]) => save('nescotech_projects', projects),
  addProject: (project: Project) => {
    const projects = load<Project[]>('nescotech_projects', INITIAL_PROJECTS);
    save('nescotech_projects', [project, ...projects]);
  },
  updateProject: (id: string, updates: Partial<Project>) => {
    const projects = load<Project[]>('nescotech_projects', INITIAL_PROJECTS);
    const updated = projects.map(p => p.id === id ? { ...p, ...updates } : p);
    save('nescotech_projects', updated);
  },
  deleteProject: (id: string) => {
    const projects = load<Project[]>('nescotech_projects', INITIAL_PROJECTS);
    save('nescotech_projects', projects.filter(p => p.id !== id));
  },

  // USERS
  getUsers: () => load<AppUser[]>('nescotech_users', INITIAL_USERS),
  addUser: (user: AppUser) => {
    const users = load<AppUser[]>('nescotech_users', INITIAL_USERS);
    save('nescotech_users', [...users, user]);
  },
  updateUser: (id: string, updates: Partial<AppUser>) => {
    const users = load<AppUser[]>('nescotech_users', INITIAL_USERS);
    const updated = users.map(u => u.id === id ? { ...u, ...updates } : u);
    save('nescotech_users', updated);
  },
  deleteUser: (id: string) => {
    const users = load<AppUser[]>('nescotech_users', INITIAL_USERS);
    save('nescotech_users', users.filter(u => u.id !== id));
  },

  // BOOKINGS
  getBookings: () => load<BookingRecord[]>('nescotech_bookings', INITIAL_BOOKINGS),
  addBooking: (booking: BookingRecord) => {
    const bookings = load<BookingRecord[]>('nescotech_bookings', INITIAL_BOOKINGS);
    save('nescotech_bookings', [booking, ...bookings]);
  },
  updateBooking: (id: string, updates: Partial<BookingRecord>) => {
    const bookings = load<BookingRecord[]>('nescotech_bookings', INITIAL_BOOKINGS);
    const updated = bookings.map(b => b.id === id ? { ...b, ...updates } : b);
    save('nescotech_bookings', updated);
  },
  updateBookingStatus: (id: string, status: any) => {
    const bookings = load<BookingRecord[]>('nescotech_bookings', INITIAL_BOOKINGS);
    const updated = bookings.map(b => b.id === id ? { ...b, status } : b);
    save('nescotech_bookings', updated);
  },
  deleteBooking: (id: string) => {
    const bookings = load<BookingRecord[]>('nescotech_bookings', INITIAL_BOOKINGS);
    save('nescotech_bookings', bookings.filter(b => b.id !== id));
  },

  // TESTIMONIALS
  getTestimonials: () => load<Testimonial[]>('nescotech_testimonials', INITIAL_TESTIMONIALS),
  saveTestimonials: (list: Testimonial[]) => save('nescotech_testimonials', list),
  addTestimonial: (t: Testimonial) => {
    const list = load<Testimonial[]>('nescotech_testimonials', INITIAL_TESTIMONIALS);
    save('nescotech_testimonials', [t, ...list]);
  },
  updateTestimonial: (id: string, updates: Partial<Testimonial>) => {
    const list = load<Testimonial[]>('nescotech_testimonials', INITIAL_TESTIMONIALS);
    const updated = list.map(t => t.id === id ? { ...t, ...updates } : t);
    save('nescotech_testimonials', updated);
  },
  deleteTestimonial: (id: string) => {
    const list = load<Testimonial[]>('nescotech_testimonials', INITIAL_TESTIMONIALS);
    save('nescotech_testimonials', list.filter(t => t.id !== id));
  },
  resetTestimonials: () => {
    save('nescotech_testimonials', INITIAL_TESTIMONIALS);
  },

  // COMPLAINTS
  getComplaints: () => load<Complaint[]>('nescotech_complaints', []),
  addComplaint: (complaint: Complaint) => {
    const complaints = load<Complaint[]>('nescotech_complaints', []);
    save('nescotech_complaints', [complaint, ...complaints]);
  },
  updateComplaint: (id: string, updates: Partial<Complaint>) => {
    const complaints = load<Complaint[]>('nescotech_complaints', []);
    const updated = complaints.map(c => c.id === id ? { ...c, ...updates } : c);
    save('nescotech_complaints', updated);
  },
  updateComplaintStatus: (id: string, status: Complaint['status']) => {
    const complaints = load<Complaint[]>('nescotech_complaints', []);
    const updated = complaints.map(c => c.id === id ? { ...c, status, resolvedAt: status === 'Resolved' || status === 'Closed' ? new Date().toISOString() : c.resolvedAt } : c);
    save('nescotech_complaints', updated);
  },
  deleteComplaint: (id: string) => {
    const complaints = load<Complaint[]>('nescotech_complaints', []);
    save('nescotech_complaints', complaints.filter(c => c.id !== id));
  },

  // SETTINGS
  getSettings: () => load<AppSettings>('nescotech_settings', INITIAL_SETTINGS),
  updateSettings: (updates: Partial<AppSettings>) => {
    const current = load<AppSettings>('nescotech_settings', INITIAL_SETTINGS);
    save('nescotech_settings', { ...current, ...updates });
  },

  // NEWSLETTER SUBSCRIBERS
  getNewsletterSubscribers: () => load<NewsletterSubscriber[]>('nescotech_newsletter', []),
  addNewsletterSubscriber: (subscriber: NewsletterSubscriber) => {
    const subscribers = load<NewsletterSubscriber[]>('nescotech_newsletter', []);
    // Check for duplicate email
    if (subscribers.some(s => s.email === subscriber.email)) {
      return false; // Already subscribed
    }
    save('nescotech_newsletter', [subscriber, ...subscribers]);
    return true;
  },
  deleteNewsletterSubscriber: (id: string) => {
    const subscribers = load<NewsletterSubscriber[]>('nescotech_newsletter', []);
    save('nescotech_newsletter', subscribers.filter(s => s.id !== id));
  },
  updateNewsletterSubscriber: (id: string, updates: Partial<NewsletterSubscriber>) => {
    const subscribers = load<NewsletterSubscriber[]>('nescotech_newsletter', []);
    const updated = subscribers.map(s => s.id === id ? { ...s, ...updates } : s);
    save('nescotech_newsletter', updated);
  },

  resetAll: () => {
    localStorage.clear();
    window.location.reload();
  }
};
